import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { optimizeLinkedIn } from '../../services/openaiService';
import { parseResumeFile } from '../../services/resumeParserService';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';
import {
  BriefcaseIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  SparklesIcon,
  LinkIcon,
  HelpIcon,
  FileTextIcon,
  AwardIcon,
  UserIcon,
} from '../icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * LinkedIn Optimizer — Electron-native pane.
 *
 * Two side-by-side panels:
 *   LEFT  — Headshot Studio. User picks their stored profile picture or
 *           uploads a new photo; we POST it to /api/headshot/generate
 *           which runs OpenAI's gpt-image-1 in edit mode and returns a
 *           polished LinkedIn-style headshot as a base64 PNG. User can
 *           preview and download the result.
 *   RIGHT — Profile Optimizer. User enters target role + industry and
 *           picks a resume source (saved Profile or fresh upload). We
 *           hit `optimizeLinkedIn()` (same service the website uses)
 *           and render section-by-section rewrites with issues, tips,
 *           and keywords.
 *
 * The optimizer service is free; the headshot is a paid AI call so the
 * UI surfaces a "Generating headshot…" state and an explicit error if
 * the backend rejects it.
 * ─────────────────────────────────────────────────────────────────────── */

const SECTIONS = [
  { id: 'headline',   label: 'Headline',         Icon: SparklesIcon },
  { id: 'summary',    label: 'About / Summary',  Icon: FileTextIcon },
  { id: 'experience', label: 'Experience',       Icon: BriefcaseIcon },
  { id: 'skills',     label: 'Skills',           Icon: HelpIcon },
  { id: 'education',  label: 'Education',        Icon: AwardIcon },
];

export default function LinkedInOptimizerPane() {
  const { user } = useUser();

  /* ── Profile data shared by both panels ─────────────────────────── */
  const [profileData, setProfileData] = useState(null);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);

  /* ── Headshot panel state ───────────────────────────────────────── */
  const [headshotSource, setHeadshotSource] = useState('profile'); // 'profile' | 'upload'
  const [uploadedPhoto, setUploadedPhoto] = useState(null);        // File
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState(null);
  const [generatedHeadshot, setGeneratedHeadshot] = useState(null);
  const [isGeneratingHeadshot, setIsGeneratingHeadshot] = useState(false);
  const [headshotError, setHeadshotError] = useState('');
  const photoInputRef = useRef(null);

  /* ── Optimizer panel state ──────────────────────────────────────── */
  const [targetRole, setTargetRole] = useState('');
  const [targetIndustry, setTargetIndustry] = useState('');

  const [resumeSource, setResumeSource] = useState('profile');      // 'profile' | 'upload'
  const [uploadedResumeName, setUploadedResumeName] = useState(''); // display only
  const [parsedResume, setParsedResume] = useState(null);           // structured profileData shape
  const [isParsingResume, setIsParsingResume] = useState(false);
  const resumeInputRef = useRef(null);

  const [results, setResults] = useState({});
  const [overallScore, setOverallScore] = useState(null);
  const [activeSection, setActiveSection] = useState('headline');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizerError, setOptimizerError] = useState('');
  const [copied, setCopied] = useState({});

  /* ── Fetch profile on mount — seeds LinkedIn URL, target role,
       avatar, and the optimizer's profileData. ──────────────────── */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !data?.profile) return;
        setProfileData(data.profile);
        setProfileAvatarUrl(data.profile.avatarUrl || null);
        if (data.profile.jobTitle) setTargetRole(prev => prev || data.profile.jobTitle);
        // If no saved avatar, default the headshot source to "upload".
        if (!data.profile.avatarUrl) setHeadshotSource('upload');
      } catch { /* network errors don't block the pane */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  /* ── Cleanup the object URL when the upload changes ──────────── */
  useEffect(() => () => {
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
  }, [uploadedPreviewUrl]);

  /* ───────────────────────────── Headshot ───────────────────────── */

  const handlePhotoSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setHeadshotError('Please choose an image file (PNG, JPG, WEBP).');
      return;
    }
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedPhoto(file);
    setUploadedPreviewUrl(URL.createObjectURL(file));
    setHeadshotError('');
    setGeneratedHeadshot(null);
  };

  const handleGenerateHeadshot = async () => {
    setHeadshotError('');
    setGeneratedHeadshot(null);

    const form = new FormData();
    if (headshotSource === 'upload') {
      if (!uploadedPhoto) {
        setHeadshotError('Choose a photo to upload first.');
        return;
      }
      form.append('photo', uploadedPhoto);
    } else {
      if (!profileAvatarUrl) {
        setHeadshotError('No profile picture on file — upload a photo instead.');
        return;
      }
      // avatarUrl looks like "/uploads/avatars/avatar_<id>_<ts>.<ext>";
      // backend resolves it under uploads/avatars/ only.
      const fname = profileAvatarUrl.split('/').pop();
      form.append('avatarPath', fname);
    }

    setIsGeneratingHeadshot(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/headshot/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok || !data?.image) {
        throw new Error(data?.message || 'Headshot generation failed.');
      }
      setGeneratedHeadshot(data.image);
    } catch (err) {
      setHeadshotError(err.message || 'Headshot generation failed.');
    } finally {
      setIsGeneratingHeadshot(false);
    }
  };

  const downloadHeadshot = () => {
    if (!generatedHeadshot) return;
    const a = document.createElement('a');
    a.href = generatedHeadshot;
    const base = (profileData?.fullName || user?.name || 'headshot')
      .replace(/\s+/g, '_');
    a.download = `${base}_headshot.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const headshotSourceReady =
    headshotSource === 'profile' ? !!profileAvatarUrl : !!uploadedPhoto;

  /* ───────────────────────────── Optimizer ──────────────────────── */

  const handleResumeUpload = async (file) => {
    if (!file) return;
    setOptimizerError('');
    setUploadedResumeName(file.name);
    setIsParsingResume(true);
    try {
      const parsed = await parseResumeFile(file, '', []);
      if (!parsed) throw new Error('Could not read the resume.');
      // Adapt parsed-resume shape to the profileData shape that
      // optimizeLinkedIn() expects.
      const adapted = {
        fullName: parsed.personalInfo?.name || '',
        email:    parsed.personalInfo?.email || '',
        phone:    parsed.personalInfo?.phone || '',
        location: parsed.personalInfo?.location || '',
        linkedIn: parsed.personalInfo?.linkedin || '',
        portfolio: parsed.personalInfo?.portfolio || '',
        professionalSummary: parsed.summary || '',
        workExperience: parsed.workExperience || [],
        education: parsed.education || [],
        skills: parsed.skills || '',
        projects: parsed.projects || [],
        certifications: parsed.certifications || '',
      };
      setParsedResume(adapted);
    } catch (err) {
      setOptimizerError(`Resume parse failed: ${err.message}`);
      setUploadedResumeName('');
    } finally {
      setIsParsingResume(false);
    }
  };

  // Shared LinkedIn Optimizer + Headshot Studio pool — both buttons in
  // this pane read the same `linkedinOptimizer` credit counter on the
  // server, so the badge value matches no matter which action ran last.
  const gate = useFeatureGate('linkedinOptimizer');

  const handleOptimize = async () => {
    if (!targetRole.trim()) {
      setOptimizerError("Enter the role you're targeting.");
      return;
    }
    const activeProfile = resumeSource === 'upload' ? parsedResume : profileData;
    if (resumeSource === 'upload' && !parsedResume) {
      setOptimizerError('Upload and wait for the resume to finish parsing.');
      return;
    }
    if (gate.locked) { gate.openPaywall(); return; }
    setIsOptimizing(true);
    setOptimizerError('');
    setResults({});
    setOverallScore(null);
    try {
      // Deduct via the shared gate. Active subscribers bypass on the
      // backend; the headshot endpoint also deducts from this same pool
      // server-side so the badge stays consistent.
      const { ok, reason } = await gate.deduct();
      if (!ok) {
        if (reason === 'no-credits') { setIsOptimizing(false); return; }
        throw new Error(reason || 'Out of LinkedIn Optimizer credits — upgrade to continue.');
      }
      const data = await optimizeLinkedIn(
        '',
        targetRole.trim(),
        targetIndustry.trim(),
        '',
        activeProfile,
      );
      const opts = data?.optimizations || {};
      setResults(opts);
      setOverallScore(typeof data?.overallScore === 'number' ? data.overallScore : null);
      const firstReady = SECTIONS.find(s => opts[s.id]);
      if (firstReady) setActiveSection(firstReady.id);
    } catch (err) {
      setOptimizerError(err.message || 'Failed to optimize. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleCopy = async (sectionId, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [sectionId]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [sectionId]: false })), 1800);
    } catch { /* ignore */ }
  };

  const hasResults = Object.keys(results).length > 0;
  const activeData = results[activeSection];
  const optimizerReady =
    targetRole.trim() &&
    (resumeSource === 'profile' ? !!profileData : !!parsedResume);

  /* ───────────────────────────── Render ─────────────────────────── */

  return (
    <div className="lop-root">
      <div className="pane">
        <div className="pane-eyebrow">
          <BriefcaseIcon size={11} /> LinkedIn Optimizer
        </div>
        <h1 className="pane-title">LinkedIn Profile Optimizer</h1>
      </div>

      <div className="lop-workspace2">
        {/* ─────────────── LEFT: Headshot Studio ─────────────── */}
        <section className="lop-card lop-headshot">
          <div className="lop-card-head">
            <span className="lop-card-eyebrow">
              <UserIcon size={11} /> Headshot Studio
            </span>
            <h2 className="lop-card-title">Generate Headshot</h2>
            <p className="lop-card-sub">
              Use your profile picture or upload a new photo. We'll generate a
              polished, professional headshot ready for LinkedIn.
            </p>
          </div>

          <div className="lop-radio-row">
            <label className={`lop-radio ${headshotSource === 'profile' ? 'active' : ''} ${!profileAvatarUrl ? 'disabled' : ''}`}>
              <input
                type="radio"
                name="headshot-src"
                checked={headshotSource === 'profile'}
                onChange={() => setHeadshotSource('profile')}
                disabled={!profileAvatarUrl || isGeneratingHeadshot}
              />
              <span>Use profile picture</span>
              {!profileAvatarUrl && <small>none on file</small>}
            </label>
            <label className={`lop-radio ${headshotSource === 'upload' ? 'active' : ''}`}>
              <input
                type="radio"
                name="headshot-src"
                checked={headshotSource === 'upload'}
                onChange={() => setHeadshotSource('upload')}
                disabled={isGeneratingHeadshot}
              />
              <span>Upload new photo</span>
            </label>
          </div>

          <div className="lop-headshot-stage">
            {/* Source photo column */}
            <div className="lop-headshot-slot">
              <div className="lop-headshot-slot-label">Source</div>
              {headshotSource === 'profile' && profileAvatarUrl ? (
                <img
                  className="lop-headshot-img"
                  src={`${API_BASE_URL}${profileAvatarUrl}`}
                  alt="Profile"
                />
              ) : headshotSource === 'upload' && uploadedPreviewUrl ? (
                <img
                  className="lop-headshot-img"
                  src={uploadedPreviewUrl}
                  alt="Upload preview"
                />
              ) : (
                <button
                  type="button"
                  className="lop-headshot-empty"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isGeneratingHeadshot || headshotSource === 'profile'}
                >
                  <FileTextIcon size={20} />
                  {headshotSource === 'upload' ? 'Choose photo' : 'No photo'}
                </button>
              )}
              {headshotSource === 'upload' && uploadedPreviewUrl && (
                <button
                  type="button"
                  className="lop-headshot-replace"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isGeneratingHeadshot}
                >
                  Replace
                </button>
              )}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handlePhotoSelect(e.target.files?.[0])}
              />
            </div>

            {/* Result column */}
            <div className="lop-headshot-slot">
              <div className="lop-headshot-slot-label">Generated</div>
              {generatedHeadshot ? (
                <img
                  className="lop-headshot-img"
                  src={generatedHeadshot}
                  alt="Generated headshot"
                />
              ) : (
                <div className={`lop-headshot-empty static ${isGeneratingHeadshot ? 'busy' : ''}`}>
                  {isGeneratingHeadshot ? (
                    <>
                      <span className="lop-spinner" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <SparklesIcon size={20} />
                      Result appears here
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {headshotError && <div className="pane-error">{headshotError}</div>}

          <div className="lop-headshot-actions">
            <button
              type="button"
              className="pane-cta"
              onClick={() => {
                if (gate.locked) { gate.openPaywall(); return; }
                handleGenerateHeadshot();
              }}
              disabled={isGeneratingHeadshot || !headshotSourceReady || gate.locked}
              title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
            >
              {isGeneratingHeadshot
                ? <>Generating headshot…</>
                : <>Generate Headshot <SparklesIcon size={13} /> <FreeCreditsLabel feature="linkedinOptimizer" /></>}
            </button>
            {generatedHeadshot && (
              <button
                type="button"
                className="pane-cta-secondary"
                onClick={downloadHeadshot}
              >
                Download
              </button>
            )}
          </div>
        </section>

        {/* ─────────────── RIGHT: Profile Optimizer ─────────────── */}
        <section className="lop-card lop-optimizer">
          <div className="lop-card-head">
            <span className="lop-card-eyebrow">
              <SparklesIcon size={11} /> Profile Optimizer
            </span>
            <h2 className="lop-card-title">Optimize my LinkedIn</h2>
            <p className="lop-card-sub">
              Target a role, pick your resume source, and we'll rewrite every
              section of your profile to match — with issues, tips, and keywords.
            </p>
          </div>

          {/* Inputs */}
          <div className="lop-form">
            <div className="lop-form-row">
              <div className="pane-field">
                <label className="pane-label">Target role *</label>
                <input
                  className="pane-input"
                  value={targetRole}
                  onChange={(e) => { setTargetRole(e.target.value); setOptimizerError(''); }}
                  placeholder="e.g. Senior Software Engineer"
                  disabled={isOptimizing}
                />
              </div>
              <div className="pane-field">
                <label className="pane-label">Target industry</label>
                <input
                  className="pane-input"
                  value={targetIndustry}
                  onChange={(e) => setTargetIndustry(e.target.value)}
                  placeholder="e.g. FinTech, Healthcare, SaaS"
                  disabled={isOptimizing}
                />
              </div>
            </div>

            {/* Resume source picker */}
            <div className="pane-field">
              <label className="pane-label">Resume source</label>
              <div className="lop-radio-row">
                <label className={`lop-radio ${resumeSource === 'profile' ? 'active' : ''} ${!profileData ? 'disabled' : ''}`}>
                  <input
                    type="radio"
                    name="resume-src"
                    checked={resumeSource === 'profile'}
                    onChange={() => setResumeSource('profile')}
                    disabled={!profileData || isOptimizing}
                  />
                  <span>Use profile data</span>
                </label>
                <label className={`lop-radio ${resumeSource === 'upload' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="resume-src"
                    checked={resumeSource === 'upload'}
                    onChange={() => setResumeSource('upload')}
                    disabled={isOptimizing}
                  />
                  <span>Upload resume</span>
                </label>
              </div>
              {resumeSource === 'upload' && (
                <div className="lop-resume-upload">
                  <input
                    ref={resumeInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    hidden
                    onChange={(e) => handleResumeUpload(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    className="pane-cta-secondary"
                    onClick={() => resumeInputRef.current?.click()}
                    disabled={isParsingResume || isOptimizing}
                  >
                    <FileTextIcon size={13} />
                    {isParsingResume ? 'Parsing…'
                      : uploadedResumeName ? `Replace (${uploadedResumeName})`
                      : 'Choose PDF / DOCX'}
                  </button>
                  {parsedResume && !isParsingResume && (
                    <span className="lop-resume-ok">
                      <CheckCircleIcon size={12} /> Parsed
                    </span>
                  )}
                </div>
              )}
            </div>

            {optimizerError && <div className="pane-error">{optimizerError}</div>}

            <button
              type="button"
              className="pane-cta lop-go-btn"
              onClick={handleOptimize}
              disabled={isOptimizing || !optimizerReady || gate.locked}
              title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
            >
              {isOptimizing ? (
                <span className="lop-loading">
                  <span className="lop-bars" aria-hidden="true">
                    <span /><span /><span /><span /><span />
                  </span>
                  Optimizing profile…
                </span>
              ) : (
                <>Optimize My LinkedIn <SparklesIcon size={13} /> <FreeCreditsLabel feature="linkedinOptimizer" /></>
              )}
            </button>
          </div>

          {/* Results */}
          {overallScore !== null && (
            <div className="lop-score-card">
              <ScoreRing value={overallScore} />
              <div className="lop-score-meta">
                <div className="lop-score-title">Profile Score</div>
                <div className="lop-score-tag">
                  {overallScore >= 80 ? 'Excellent profile strength'
                    : overallScore >= 60 ? 'Good — apply suggestions below'
                    : 'Needs improvement — start with the issues list'}
                </div>
              </div>
            </div>
          )}

          {hasResults && (
            <div className="lop-tabs">
              {SECTIONS.map((s) => {
                const Icon = s.Icon;
                const ready = !!results[s.id];
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`lop-tab ${activeSection === s.id ? 'active' : ''} ${ready ? 'ready' : ''}`}
                    onClick={() => setActiveSection(s.id)}
                  >
                    <Icon size={13} />
                    <span>{s.label}</span>
                    {ready && <span className="lop-tab-dot" />}
                  </button>
                );
              })}
            </div>
          )}

          {hasResults && activeData ? (
            <div className="lop-result-card">
              <div className="lop-result-head">
                <h3>
                  {SECTIONS.find(s => s.id === activeSection)?.label} optimization
                </h3>
                <button
                  type="button"
                  className="pane-cta-secondary lop-copy-btn"
                  onClick={() => handleCopy(activeSection, activeData?.optimized || '')}
                >
                  {copied[activeSection]
                    ? <><CheckCircleIcon size={13} /> Copied</>
                    : <><LinkIcon size={13} /> Copy</>}
                </button>
              </div>

              {activeData?.issues?.length > 0 && (
                <div className="lop-section-block lop-issues">
                  <div className="lop-section-label">
                    <AlertTriangleIcon size={13} />
                    <span>Current issues</span>
                  </div>
                  <ul>
                    {activeData.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                  </ul>
                </div>
              )}

              <div className="lop-section-block lop-optimized">
                <div className="lop-section-label">
                  <SparklesIcon size={13} />
                  <span>Optimized version</span>
                </div>
                <div className="lop-optimized-text">{activeData?.optimized}</div>
              </div>

              {activeData?.tips?.length > 0 && (
                <div className="lop-section-block lop-tips">
                  <div className="lop-section-label">
                    <HelpIcon size={13} />
                    <span>Pro tips</span>
                  </div>
                  <ul>
                    {activeData.tips.map((tip, i) => <li key={i}>{tip}</li>)}
                  </ul>
                </div>
              )}

              {activeData?.keywords?.length > 0 && (
                <div className="lop-section-block lop-keywords">
                  <div className="lop-section-label">
                    <SparklesIcon size={13} />
                    <span>Keywords to include</span>
                  </div>
                  <div className="lop-kw-chips">
                    {activeData.keywords.map((kw, i) => (
                      <span key={i} className="lop-kw-chip">{kw}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────── */

function ScoreRing({ value }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const circ = 2 * Math.PI * 38;
  const dash = (pct / 100) * circ;
  const color =
    pct >= 80 ? 'var(--es-success)' :
    pct >= 60 ? 'var(--es-warning)' :
                'var(--es-danger)';
  return (
    <div className="lop-score-circle">
      <svg viewBox="0 0 100 100" width="92" height="92" aria-hidden="true">
        <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="9" />
        <circle
          cx="50" cy="50" r="38" fill="none"
          stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="lop-score-value">{pct}<span>/100</span></div>
    </div>
  );
}
